import React from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "antd";
import { useQuery } from "@tanstack/react-query";
import useAdmin from "../../hooks/useAdmin";
import { retryUnlessClientError, serverErrorMessage } from "../../lib/serverError";
import { AdminPageHeader } from "../../components/admin-shell/AdminShell";
import "./home.css";

const formatNumber = (n) => Number(n || 0).toLocaleString();

const StatTile = ({ label, value, hint, to }) => {
  const body = (
    <>
      <span className="adm-stat__label">{label}</span>
      <span className="adm-stat__value">{formatNumber(value)}</span>
      {hint ? <span className="adm-stat__hint">{hint}</span> : null}
    </>
  );
  return to ? (
    <Link to={to} className="adm-card adm-stat adm-stat--link">
      {body}
    </Link>
  ) : (
    <div className="adm-card adm-stat">{body}</div>
  );
};

const Home = () => {
  const { getStats } = useAdmin();
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: getStats,
    refetchOnWindowFocus: false,
    retry: retryUnlessClientError,
  });

  const groups = Object.entries(stats?.bloodGroups || {}).sort((a, b) => b[1] - a[1]);
  const largest = groups.length ? groups[0][1] : 0;

  return (
    <>
      <AdminPageHeader title="Dashboard" subtitle="Donors, donations and requests at a glance." />

      {error ? (
        <div className="adm-card adm-empty">Could not load the dashboard. {serverErrorMessage(error, "")}</div>
      ) : null}

      {isLoading ? (
        <div className="adm-stats">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="adm-card adm-stat">
              <Skeleton active title={false} paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      ) : stats ? (
        <>
          <div className="adm-stats">
            <StatTile label="Total donors" value={stats.totalDonors} hint={`${formatNumber(stats.banned)} blocked`} to="/allDonor" />
            <StatTile label="Ready to donate now" value={stats.readyNow} hint="No donation in the last 90 days" />
            <StatTile label="Joined this month" value={stats.newThisMonth} />
            <StatTile label="Bags donated" value={stats.bagsDonated} />
            <StatTile label="Blood requests" value={stats.requests} hint={`${formatNumber(stats.bagsRequested)} bags requested`} />
            <StatTile label="Suggestions" value={stats.suggestions} to="/others" />
          </div>

          <h2 className="adm-section-title">Donors by blood group</h2>
          <div className="adm-card adm-bars">
            {groups.map(([group, count]) => {
              const share = stats.totalDonors ? Math.round((count / stats.totalDonors) * 100) : 0;
              return (
                <Link
                  key={group}
                  to={`/allDonor?blood=${encodeURIComponent(group)}`}
                  className="adm-bar"
                  title={`${group}: ${formatNumber(count)} donors (${share}% of all donors)`}
                >
                  <span className="adm-bar__label">{group}</span>
                  <span className="adm-bar__track">
                    <span
                      className="adm-bar__fill"
                      style={{ width: largest ? `${Math.max((count / largest) * 100, count ? 1.5 : 0)}%` : 0 }}
                    />
                  </span>
                  <span className="adm-bar__value">
                    {formatNumber(count)}{" "}
                    <span className="adm-bar__share">{count > 0 && share === 0 ? "<1" : share}%</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </>
      ) : null}
    </>
  );
};

export default Home;
